import { useIntl } from "react-intl";

import { ImpactCategory } from "../../../__generated__/globalTypes";
import { SalesDashboardSummaryData } from "../../../data-store";
import {
  convertUnitsForDisplay,
  massUnits,
  landUnits,
  waterUnits,
  Unit,
} from "../../../util/units";
import Label from "../../labels/Label";
import ReportSummaryCard from "../../ReportSummaryCard";
import { ProportionsChartImpactRatingInfos } from "../../scope-3/ImpactRatingProportionsChart";
import { Carousel } from "../../utils/Carousel";
import {
  GhgEmissions,
  Scales,
  LandUse,
  WaterUse,
  Products,
} from "../../utils/Vectors/illustrations";
import "./SalesSummaries.css";
import SalesSummaryIcon from "../../utils/Vectors/SalesSummaryIcon";
import { useImpactCategory } from "../ImpactCategoryProvider";

interface SalesSummariesProps {
  summary: SalesDashboardSummaryData;
  impactRatingInfos: ProportionsChartImpactRatingInfos["impactRatingInfos"];
}

const INFO_MAP: Record<ImpactCategory, EffectType> = {
  GHG: "GHG_PER_KG",
  LAND_USE: "LAND_USE_PER_KG",
  WATER_USE: "WATER_USE_PER_KG",
};

type ImpactRating = "VLOW" | "LOW" | "MEDIUM" | "HIGH" | "VHIGH";

type EffectType =
  | "GHG_PER_KG"
  | "GHG_PER_SERVING"
  | "LAND_USE_PER_KG"
  | "LAND_USE_PER_SERVING"
  | "WATER_USE_PER_KG"
  | "WATER_USE_PER_SERVING";

const getImpactRating = (
  category: ImpactCategory,
  value: number,
  ratings: ProportionsChartImpactRatingInfos["impactRatingInfos"]
): ImpactRating | undefined => {
  const effectType = INFO_MAP[category];
  const entry = ratings.find((r) => r.effectType === effectType);
  if (!entry) return;

  return entry.impactRatingInfo
    .filter((i) => i.lowerBound === null || value >= i.lowerBound)
    .sort(
      (a, b) => (b.lowerBound ?? -Infinity) - (a.lowerBound ?? -Infinity)
    )[0]?.impactRating;
};

const getDynamicTitle = (
  intl: ReturnType<typeof useIntl>,
  impactCategory: ImpactCategory,
  type: "total" | "averageSales" | "averageProducts"
) => {
  const titleMap: Record<ImpactCategory, Record<typeof type, string>> = {
    GHG: {
      total: intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:totalGhgEmissions",
        defaultMessage: "Total GHG Emissions",
      }),
      averageSales: intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:averageGhgIntensitySales",
        defaultMessage: "Average GHG Intensity of Sales",
      }),
      averageProducts: intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:averageGhgIntensityProducts",
        defaultMessage: "Average GHG Intensity of Products",
      }),
    },
    LAND_USE: {
      total: intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:totalLandUse",
        defaultMessage: "Total Land Use",
      }),
      averageSales: intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:averageLandUseIntensitySales",
        defaultMessage: "Average Land Use Intensity of Sales",
      }),
      averageProducts: intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:averageLandUseIntensityProducts",
        defaultMessage: "Average Land Use Intensity of Products",
      }),
    },
    WATER_USE: {
      total: intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:totalWaterUse",
        defaultMessage: "Total Water Use",
      }),
      averageSales: intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:averageWaterIntensitySales",
        defaultMessage: "Average Water Use Intensity of Sales",
      }),
      averageProducts: intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:averageWaterUseIntensityProducts",
        defaultMessage: "Average Water Use Intensity of Products",
      }),
    },
  };

  return titleMap[impactCategory][type];
};

const SalesSummaries = ({
  summary,
  impactRatingInfos,
}: SalesSummariesProps) => {
  const intl = useIntl();
  const { impactCategory } = useImpactCategory();

  const getFormattedValueAndUnit = (
    value: number,
    category: ImpactCategory,
    isTotal: boolean = true
  ) => {
    if (category === "GHG") {
      const baseUnit =
        massUnits.find((unit) => summary.ghgEmissionsUnit?.includes(unit)) ||
        "kg";
      const converted = convertUnitsForDisplay(
        intl,
        value,
        baseUnit,
        summary.ghgEmissionsUnit.filter((unit) => unit !== baseUnit),
        isTotal ? null : "kg"
      );
      return {
        value: converted.convertedValue,
        unit: converted.unitString,
      };
    } else if (category === "WATER_USE") {
      const baseUnit =
        waterUnits.find((unit) =>
          summary.waterUseUnit?.includes(unit.toLowerCase() as Unit)
        ) || "L";
      const converted = convertUnitsForDisplay(
        intl,
        value,
        baseUnit,
        summary.waterUseUnit.filter((unit) => unit !== baseUnit) ?? [],
        isTotal ? null : "kg"
      );
      return {
        value: converted.convertedValue,
        unit: converted.unitString,
      };
    } else if (category === "LAND_USE") {
      const baseUnit =
        landUnits.find((unit) => summary.landUseUnit?.includes(unit)) || "m2";
      const converted = convertUnitsForDisplay(
        intl,
        value,
        baseUnit,
        summary.landUseUnit.filter((unit) => unit !== baseUnit) ?? [],
        isTotal ? null : "kg"
      );
      return {
        value: converted.convertedValue,
        unit: converted.unitString,
      };
    }
    return { value: value.toString(), unit: "" };
  };

  const iconMap: Record<ImpactCategory, JSX.Element> = {
    GHG: <GhgEmissions width={32} />,
    LAND_USE: <LandUse width={32} />,
    WATER_USE: <WaterUse width={32} />,
  };

  const totalValue =
    summary[
      impactCategory === "GHG"
        ? "totalGhgEmissions"
        : impactCategory === "LAND_USE"
        ? "totalLandUse"
        : "totalWaterUse"
    ]?.value;

  const averageSalesValue =
    summary[
      impactCategory === "GHG"
        ? "averageGhgIntensitySales"
        : impactCategory === "LAND_USE"
        ? "averageLandUseIntensitySales"
        : "averageWaterUseIntensitySales"
    ]?.value;

  const averageProductsValue =
    summary[
      impactCategory === "GHG"
        ? "averageGhgIntensityProducts"
        : impactCategory === "LAND_USE"
        ? "averageLandUseIntensityProducts"
        : "averageWaterUseIntensityProducts"
    ]?.value;

  const totalFormatted = getFormattedValueAndUnit(totalValue, impactCategory);
  const averageSalesFormatted = getFormattedValueAndUnit(
    averageSalesValue,
    impactCategory,
    false
  );
  const averageProductsFormatted = getFormattedValueAndUnit(
    averageProductsValue,
    impactCategory,
    false
  );

  const massFormatted = convertUnitsForDisplay(
    intl,
    summary.totalMass?.value,
    "kg",
    [],
    null
  );

  const reportSummaryCards = [
    <ReportSummaryCard
      containerClassName="SalesSummaries_ReportSummaryCard"
      key="total"
      icon={iconMap[impactCategory]}
      title={getDynamicTitle(intl, impactCategory, "total")}
      value={totalFormatted.value}
      unit={totalFormatted.unit}
    />,
    <ReportSummaryCard
      infoText={intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:averageSalesInfo/tooltip",
        defaultMessage:
          "Average impact of each sale, weighted by the number of sales of each product.",
      })}
      containerClassName="SalesSummaries_ReportSummaryCard"
      key="averageSales"
      icon={
        <Label
          colourSetting="colour"
          type="letterRating"
          impactCategory={impactCategory}
          impactRating={
            getImpactRating(
              impactCategory,
              averageSalesValue,
              impactRatingInfos
            ) as any
          }
          width={46}
          locale={null}
        />
      }
      title={getDynamicTitle(intl, impactCategory, "averageSales")}
      value={averageSalesFormatted.value}
      unit={intl.formatMessage(
        {
          id: "components/sales-dashboard/SalesSummaries:averageSalesUnit",
          defaultMessage: "{unit} sold",
        },
        {
          unit: averageSalesFormatted.unit,
        }
      )}
    />,
    <ReportSummaryCard
      infoText={intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:averageProductsInfo/tooltip",
        defaultMessage:
          "Average impact of each individual product, limited to products sold at least once.",
      })}
      containerClassName="SalesSummaries_ReportSummaryCard"
      key="averageProducts"
      icon={
        <Label
          colourSetting="colour"
          type="letterRating"
          impactCategory={impactCategory}
          impactRating={
            getImpactRating(
              impactCategory,
              averageProductsValue,
              impactRatingInfos
            ) as any
          }
          width={46}
          locale={null}
        />
      }
      title={getDynamicTitle(intl, impactCategory, "averageProducts")}
      value={averageProductsFormatted.value}
      unit={averageProductsFormatted.unit}
    />,
    <ReportSummaryCard
      containerClassName="SalesSummaries_ReportSummaryCard"
      key="veryLowRatedSales"
      icon={
        <Label
          colourSetting="colour"
          type="letterRating"
          impactCategory={impactCategory}
          impactRating="VLOW"
          width={46}
          locale={null}
        />
      }
      title={intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:veryLowRatedSales",
        defaultMessage: "Very Low Rated Sales",
      })}
      value={`${intl.formatNumber(
        summary[
          impactCategory === "GHG"
            ? "veryLowRatedSalesGhgPercentage"
            : impactCategory === "LAND_USE"
            ? "veryLowRatedSalesLandUsePercentage"
            : "veryLowRatedSalesWaterUsePercentage"
        ]?.value,
        { maximumFractionDigits: 2 }
      )}%`}
    />,
    <ReportSummaryCard
      containerClassName="SalesSummaries_ReportSummaryCard"
      key="products"
      icon={<Products width={32} />}
      title={intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:products",
        defaultMessage: "Products",
      })}
      value={intl.formatNumber(summary.numberOfProducts?.value)}
      unit={intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:productsUnit",
        defaultMessage: "on offer",
      })}
    />,
    <ReportSummaryCard
      containerClassName="SalesSummaries_ReportSummaryCard"
      key="sales"
      icon={<SalesSummaryIcon width={32} />}
      title={intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:sales",
        defaultMessage: "Sales",
      })}
      value={intl.formatNumber(summary.numberOfSales?.value)}
      unit={intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:salesUnit",
        defaultMessage: "products",
      })}
    />,
    <ReportSummaryCard
      containerClassName="SalesSummaries_ReportSummaryCard"
      key="mass"
      icon={<Scales width={32} />}
      title={intl.formatMessage({
        id: "components/sales-dashboard/SalesSummaries:mass",
        defaultMessage: "Mass",
      })}
      value={massFormatted.convertedValue}
      unit={massFormatted.unitString}
    />,
  ];

  return (
    <>
      <h3>
        {intl.formatMessage({
          id: "components/sales-dashboard/SalesSummaries:summary",
          defaultMessage: "Summary",
        })}
      </h3>
      <Carousel
        containerStyles={{
          height: "auto",
          width: "calc(100% + 25px)",
          marginLeft: "-25px",
        }}
        items={reportSummaryCards}
        footnote={<div></div>}
        carouselClassName="carouselClassName"
        buttonGroupClassName="carouselButtonGroupClassName"
        itemClassName="Carousel_ItemSalesSummary"
        partialVisibilityGutter={20}
        numbersOfItems={3}
      />
    </>
  );
};

export default SalesSummaries;
